import {
    createAction,
    createSlice,
    createAsyncThunk,
    PayloadAction,
} from '@reduxjs/toolkit'
import {
    IOrganization,
    IShareOrganization,
} from '../../components/Layout/Navbar/interfaces'
import { fetchProgramOrgsByOrgId } from '../../services/entityInstanceServices'
import { UserPermissions } from '../../constants/constants'

interface OrgState {
    shareOrgList: IShareOrganization[]
    org: IOrganization | null
    permissions: UserPermissions[]
    enabledApplications: string[]
    loadingPermissions: boolean
    hasNoOrganizations: boolean
    /** UBQ-5579: the selected org shows the Registry Tool page; null until its permissions have been read. */
    registryToolOnly: boolean | null
}

const initialState: OrgState = {
    shareOrgList: [],
    org: null,
    permissions: [],
    enabledApplications: [],
    loadingPermissions: false,
    hasNoOrganizations: false,
    registryToolOnly: null,
}

/**
 * Dispatched when the selected org shows no portal (Registry Tool only): slices that hold per-org data empty
 * themselves, so nothing from the previous org stays behind. Not sent on a switch to a full portal: the screens
 * still mounted would refetch as their data disappears, and the new org's load overwrites it anyway.
 */
export const resetOrgScopedData = createAction('org/resetOrgScopedData')

export const updateSharedOrgsList = createAsyncThunk(
    'org/updateSharedOrgsList',
    async (orgId: string) => {
        const response = await fetchProgramOrgsByOrgId(orgId || '')
        return response
    },
)

const orgSlice = createSlice({
    name: 'org',
    initialState,
    reducers: {
        updateOrg(state, action) {
            state.org = action.payload
        },
        updatePermissions(state, action) {
            state.permissions = action.payload
        },
        updateEnabledApplications(state, action) {
            state.enabledApplications = action.payload
        },
        updateLoadingPermissions(state, action) {
            state.loadingPermissions = action.payload
        },
        /**
         * Flags an authenticated user who belongs to zero organizations
         * (an "orphan" user, e.g. one removed from their last org). Used to
         * show the no-organization message instead of an endless loader.
         */
        setHasNoOrganizations(state, action: PayloadAction<boolean>) {
            state.hasNoOrganizations = action.payload
        },
        updateRegistryToolOnly(state, action: PayloadAction<boolean>) {
            state.registryToolOnly = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(resetOrgScopedData, (state) => {
                state.shareOrgList = []
            })

            .addCase(
                updateSharedOrgsList.fulfilled,
                (state, action: PayloadAction<IShareOrganization[]>) => {
                    state.shareOrgList = action.payload
                },
            )
            .addCase(updateSharedOrgsList.rejected, (state) => {
                state.shareOrgList = []
            })
    },
})

export const {
    updateOrg,
    updatePermissions,
    updateEnabledApplications,
    updateLoadingPermissions,
    setHasNoOrganizations,
    updateRegistryToolOnly,
} = orgSlice.actions

export default orgSlice.reducer
